import '@sass/_results.scss';
import '@sass/showresult/_results.scss';
import { EventDispatcher, arrayMergePrimitive, classList, copyToClipboard, gebi, genUID, getTimeText, getUrlVars, isEmptyObject, isMobileView, isSafari, l, lLink, lc, linkPrefix, logEvent, lreplace, qs, setUrlParameter, shareClick, urlParameterDelete } from '@utils/toolbox';
import { dateFormat, dateParseISO } from '@utils/dateFns';
import { eventCategories, events } from '@js/utils/event';
import { renderJourneys, renderResultAgentsViewMore } from '@components/resultsRenderTemplate';
import { Base64 } from '@js/plugins/base64';
import { ErrorTracker } from '@utils/errorTracker';
import { Modal } from '@components/modal';
import { favouriteStatus, insertIf } from './resultsRenderHelpers';
import { renderModal } from '@components/resultsRenderModalTemplate';
import { toaster } from '@js/components/toast';
import { trackEvent } from '@utils/googleDataLayer';
import { useResultQueryStore } from '@/src/vue/components/Filters/store/resultQueryStore';
import { ConnectApi } from '@js/plugins/connectApi';

const resultQueryStore = useResultQueryStore();

export const resultsRender = {
	initialised: false,
	elems: {
		sharedResultContainer: gebi('shared-result-container'),
		sharedResultInnerContainer: qs('#shared-result-container .sharedResult'),
		sharedResultCloseBtn: qs('#shared-result-container .sharedResult__closeBtn'),
		sharedResult: qs('#shared-result-container .sharedResult__result')
	},
	response: resultQueryStore.state.response,
	searchParams: {
		filters: {}
	},
	isMultiCitySearch: window?.js_params?.returntrip === 2,
	isYouthSearch: !!window?.js_params?.isYouthSearch,
	isSharedResultView: typeof window?.js_params?.sharedOrgIndex === 'number',
	missingResultDetails: {
		aircraftTypes: [],
		airlines: [],
		airports: []
	},
	missingResultDetailsTrackers: {
		/**
		 * @type {ErrorTracker}
		 */
		aircraftTypes: new ErrorTracker(),
		/**
		 * @type {ErrorTracker}
		 */
		airlines: new ErrorTracker(),
		/**
		 * @type {ErrorTracker}
		 */
		airports: new ErrorTracker()
	},
	// passengers count excluding infants
	get numberOfBags () {
		if (window?.searchBox?.getNumberOfBags) {
			return window.searchBox.getNumberOfBags();
		}
		return window?.js_params?.passengers - window?.js_params?.childrenAges?.filter?.(age => parseInt(age) === 0)?.length || 0;
	},
	get isBaggageIncludedFiltered () {
		return Boolean(this.searchParams?.filters?.baggageIncluded);
	},
	get isFlexTicketFiltered () {
		return Boolean(this.searchParams?.filters?.flexTicket);
	},
	get isCabinBaggageIncludedFiltered () {
		return Boolean(this.searchParams?.filters?.cabinBaggageIncluded);
	},
	get isClimateCompensatedFiltered () {
		return Boolean(this.searchParams?.filters?.climateCompensated);
	},
	get isEmissionSorting () {
		return this.searchParams.sorting === 'emission';
	},
	get isYouthFiltered () {
		return Boolean(this.searchParams?.filters?.youthTicket);
	},
	bindEvents () {
		// Disable browser back button when result modal is open on mobile
		if (typeof window.history !== 'undefined' && this.isMobile) {
			// history.pushState(null, null, document.URL);

			window.addEventListener('popstate', () => {
				if (this.resultModal?.isOpen) {
					this.resultModal.close();

					// history.pushState(null, null, document.URL);
				}
			}, { once: true });
		}

		// If the url contains a shared
		if (this.isSharedResultView) {
			document.body.classList.add('shared-result');
		}

		if (this.elems.sharedResultCloseBtn) {
			this.elems.sharedResultCloseBtn.addEventListener('click', this.removeSharedResult.bind(this));
		}

		document.addEventListener('click', (event) => {
			/* eslint-disable no-useless-return */
			const elem = event.target;

			if (elem.closest('.abResult--package')) {
				console.log(elem.dataset.responseId);
				event.stopPropagation();
				this.buildPackageModal(elem.dataset.responseId).open();
				return;
			}

			// Share button
			if (elem.closest('.copyUrl')) {
				const sharedUrlInput = elem.closest('.shareBtn--manualCopy')?.querySelector('.sharedUrl__field');
				if (sharedUrlInput) {
					sharedUrlInput.select();
					document.execCommand('copy');
					sharedUrlInput.classList.add('copied');
				}

				logEvent(events.shareResult, eventCategories.share);

				return;
			} else if (elem.closest('.shareBtn--manualCopy')) {
				return;
			} else if (elem.closest('.abResult__shareBtn')) { // Closest elem .abResult__shareBtn
				const shareBtn = elem.closest('.abResult__shareBtn');

				const sharedResultURL = elem.closest('.abResult__shareBtn--package')
					? window.location.host + shareBtn.dataset.url
					: this.getSharedResultURL(shareBtn.dataset.result);

				shareClick({ title: window?.js_params?.domain_title, url: sharedResultURL }).catch((error) => {
					if (error.name === 'AbortError') {
						return;
					}
					copyToClipboard(sharedResultURL).then(() => {
						console.log('copytoclipboard toast new');
						toaster.newToast(l('showresult.share.linkCopied'), 'info', 'top');
					}).catch((error) => {
						console.log('Clipboard copy failed: ', error);
						this.openManualCopyPopup(shareBtn, sharedResultURL);
					});
				});

				logEvent(events.shareResult, eventCategories.share);

				return;
			}

			// Any tooltip
			if (elem.closest('.icon-circle-question-mark:not(.abIataTimespan__time--decoration--icon)')) {
				event.stopPropagation();
				event.preventDefault();

				if (elem.closest('.icon-circle-question-mark').parentElement.querySelector('.question-mark--text')) {
					if (elem.closest('.icon-circle-question-mark').parentElement.querySelector('.active')) {
						elem.closest('.icon-circle-question-mark').parentElement.querySelector('.question-mark--text').classList.remove('active');
					} else {
						elem.closest('.icon-circle-question-mark').parentElement.querySelector('.question-mark--text').classList.add('active');
					}
				}

				return;
			}

			// Buy button
			if (elem.classList.contains('otaLink')) {
				const source = elem.closest('.abResult--modal') ? 'modal' : new URL(elem.href).searchParams.get('source');
				// Get source from link url parameter

				// Prevent the default action
				// event.preventDefault();

				// Update the url to include the eventId
				const url = new URL(elem.href);
				const eventId = genUID();
				url.searchParams.set('eventId', eventId);

				// Get the index of the result
				const index = elem.closest('.abResult').dataset.index;

				if (!document.body.classList.contains('lastminute')) {
					if (source === 'modal') {
						logEvent(events.clickoutResultViewMore, eventCategories.clickout);
					} else {
						logEvent(events.clickoutResultList, eventCategories.clickout);
					}
				}

				// Send the event to the server
				this.outclickAgencyResult(resultsRender?.response?.flights[index], index, eventId);

				// window.open(url.href, '_blank');
				return;
			}

			// Buy buttton
			if (elem.closest('.abBuyBtn')) {
				event.stopPropagation();
				return;
			}

			if (elem.closest('.abResult__emission')) {
				event.stopPropagation();
				return;
			}

			// Show all quotes
			if (elem.closest('[data-action="view-all-agencies"]')) {
				const clickedResult = elem.closest('.abResult');
				const otas = qs('.abResult__otaAndBuyAll', clickedResult);
				const icon = qs('.showAllAgencies span', clickedResult);

				const isOtasVisible = otas.classList.contains('abResult__otaAndBuyAll--expanded');

				if (isOtasVisible) {
					otas.classList.remove('abResult__otaAndBuyAll--expanded');
					clickedResult.classList.remove('abResult--expandedOtas');
					icon.classList.replace('icon-chevron-up', 'icon-chevron-down');

					otas.innerHTML = '';

					logEvent(events.viewlessOTAs, eventCategories.viewmore, window?.showresult?.activeView);
				} else {
					const resultIndex = Number(clickedResult.dataset.index);

					const source = window?.showresult?.activeView || 'list';

					const flight = this.getFlightRenderData(resultsRender.response.flights[resultIndex], resultIndex);

					otas.innerHTML = renderResultAgentsViewMore(flight, source);

					icon.classList.replace('icon-chevron-down', 'icon-chevron-up');
					otas.classList.add('abResult__otaAndBuyAll--expanded');
					clickedResult.classList.add('abResult--expandedOtas');

					logEvent(events.viewmoreOTAs, eventCategories.viewmore, window?.showresult?.activeView);
				}

				// eslint-disable-next-line no-useless-return
				return;
			}

			// Trip details toggle
			if (
				elem.closest('[data-action="toggle-trip-details"]')
				|| elem.closest('.abResult:not(.abResult--modal)')
				|| elem.closest('abResult__otaAndBuyPreview:not(abResult__otaAndBuyPreview--best)')
			) {
				if (elem.closest('.abResult__flightFavBtn')) {
					return;
				}

				if (elem.classList.contains('abResult__otaAndBuyPreview') && window?.js_params?.searchDateIsTooOld) {
					return;
				}

				const clickedResult = elem.closest('.abResult');

				// If click is on the favourites page
				if (window?.js_params.controllerClass == 'page\\favorite') {
					const index = Number(clickedResult.dataset.index);
					const flight = resultsRender.response.flights[index];
					const className = favouriteStatus(flight) + ' modal--resultModal';
					this.renderModal(flight.favouriteMeta.responseId, flight, index, flight.favouriteMeta.filters, { className }, {}, 'favourites').open();
					return;
				}

				if (!isMobileView() && clickedResult.classList.contains('abResult--expanded')) {
					clickedResult.classList.remove('abResult--expanded');

					if (this.resultModal && this.resultModal.isOpen) {
						this.resultModal.close();
					}

					logEvent(events.viewlessResult, eventCategories.viewmore);
				} else {
					resultsRender.toggleTripDetails(clickedResult);

					logEvent(events.viewmoreResult, eventCategories.viewmore);
				}

				return;
			}
		},
		false
		);
		/* eslint-enable no-useless-return */
	},
	/**
	 *
	 * @param {String} template html string to display in the modal
	 * @param {Number} index Index of the result
	 * @returns {Object} a new instance of Modal
	 */
	buildResultModal (template, index) {
		const footer = `
<button class="abResult__shareBtn abBtn abBtn--flex" data-result="${index}">
	<div class="shareBtn__popup"></div>
	<span class="icon icon-share-2"></span>
	<span>${l('global.simplePhrases.share')}</span>
</button>
<button class="js-close-modal abBtn abBtn--flex abBtn--blueWhite">
	<span>${l('global.simplePhrases.close')}</span>
</button>
		`;

		const resizeEvent = (e) => {
			if (isMobileView()) {
				this.resultModal.html.style.display = '';
				document.body.classList.add('modal-open');
			} else {
				this.resultModal.html.style.display = 'none';
				document.body.classList.remove('modal-open');
			}
		};

		this.resultModal = new Modal({
			body: template,
			className: 'modal--resultModal',
			footer: footer,
			hideHeader: true,
			hideMobileUp: true,
			hideOnSwipe: true,
			hideOnHistoryBack: isMobileView(),
			id: 'resultModal',
			onClose: () => {
				// Remove custom event listener.
				window.removeEventListener('resize', resizeEvent);
				this.resultModal = null;
			},
			onInit: () => window.addEventListener('resize', resizeEvent),
			resetScroll: true
		});

		return this.resultModal;
	},
	async buildPackageModal (resultId, origin) {
		// Get package deal from api
		const packageDeal = await ConnectApi('Charter', 'getPackageDealById', [{ id: resultId, origin }]);
		const resizeEvent = (e) => {
			classList(document.body).toggle('modal-open', isMobileView());
		};

		packageDeal.outbound_time = getTimeText(parseFloat(dateFormat(new Date(packageDeal.outbound_arrival) - new Date(packageDeal.outbound_departure), 't')));
		packageDeal.inbound_time = getTimeText(parseFloat(dateFormat(new Date(packageDeal.inbound_arrival) - new Date(packageDeal.inbound_departure), 't')));

		this.resultModal = new Modal({
			header: `
	<span>${lreplace('resultModal.packageDeal.title', { destination: packageDeal.from_city + ' - ' + packageDeal.to_city })}</span>
			`,
			body: `
<div class="abResult abResult--modal abResult--modalPackage" data-index="0">
	<div class="abResult__legs abResult__legs--default">
		<div class="abResult__detailed">
			<div class="abResult__head">
				<div>
					<span class="icon icon-circle-plane-up"></span>
					<span>${packageDeal.from_city} – ${packageDeal.to_city}</span>
				</div>
			</div>
			<div class="abResult__legs">
				<div class="abResult__leg">
					<div class="abResult__leg--airlinepicture">
					  <img
						src="/static/v${js_params.version}/images/airlines/iata/small/${packageDeal.outbound_airline}.png"
						class="abResult__airlineImg"
						title="${packageDeal.airline}" alt="${packageDeal.airline}"
						onerror="this.style.display='none'" />
					  <div class="abResult__flightTime"><span>${packageDeal.outbound_time}</span></div>
					</div>
					<div class="abResult__leg--divider"><div class="abResult__line"></div></div>
					<div class="abResult__leg--dates">
						<div>
							<div class="abResult__smallLabel"><span>${dateFormat(new Date(packageDeal.outbound_departure), 'd MMM yyyy')}</span></div>
							<div><span class="abResult__bigLabel">${dateFormat(new Date(packageDeal.outbound_departure), 'HH:mm')}</span></div>
						</div>
						<div>
							<div class="abResult__smallLabel"><span>${dateFormat(new Date(packageDeal.outbound_arrival), 'd MMM yyyy')}</span></div>
							<div><span class="abResult__bigLabel">${dateFormat(new Date(packageDeal.outbound_arrival), 'HH:mm')}</span></div>
						</div>
					</div>
					<div class="abResult__leg--divider-second"><div class="abResult__line"></div></div>
					<div class="abResult__leg--destinations">
						<div class="abResult__leg--destination">
							<div class="abResult__smallLabel">
							<span>${packageDeal.from_airport_name}, ${packageDeal.from_iata}</span>
							</div>
							<div><span class="abResult__bigLabel">${packageDeal.from_city}</span></div>
						</div>
						<div class="abResult__leg--destination">
							<div class="abResult__smallLabel">
							<span>${packageDeal.to_airport_name}, ${packageDeal.to_iata}</span>
							</div>
							<div><span class="abResult__bigLabel">${packageDeal.to_city}</span></div>
						</div>
					</div>
				</div>
			</div>
			<div class="abResult__head">
				<div>
					<span class="icon icon-circle-plane-down"></span>
					<span>${packageDeal.to_city} – ${packageDeal.from_city}</span>
				</div>
			</div>
			<div class="abResult__legs">
				<div class="abResult__leg">
					<div class="abResult__leg--airlinepicture">
					  <img
						src="/static/v${js_params.version}/images/airlines/iata/small/${packageDeal.inbound_airline}.png"
						class="abResult__airlineImg"
						title="${packageDeal.airline}" alt="${packageDeal.airline}"
						onerror="this.style.display='none'" />
					  <div class="abResult__flightTime"><span>${packageDeal.inbound_time}</span></div>
					</div>
					<div class="abResult__leg--divider"><div class="abResult__line"></div></div>
					<div class="abResult__leg--dates">
						<div>
							<div class="abResult__smallLabel"><span>${dateFormat(new Date(packageDeal.inbound_departure), 'd MMM yyyy')}</span></div>
							<div><span class="abResult__bigLabel">${dateFormat(new Date(packageDeal.inbound_departure), 'HH:mm')}</span></div>
						</div>
						<div>
							<div class="abResult__smallLabel"><span>${dateFormat(new Date(packageDeal.inbound_arrival), 'd MMM yyyy')}</span></div>
							<div><span class="abResult__bigLabel">${dateFormat(new Date(packageDeal.inbound_arrival), 'HH:mm')}</span></div>
						</div>
					</div>
					<div class="abResult__leg--divider-second"><div class="abResult__line"></div></div>
					<div class="abResult__leg--destinations">
						<div class="abResult__leg--destination">
							<div class="abResult__smallLabel">
							<span>${packageDeal.to_airport_name}, ${packageDeal.to_iata}</span>
							</div>
							<div><span class="abResult__bigLabel">${packageDeal.to_city}</span></div>
						</div>
						<div class="abResult__leg--destination">
							<div class="abResult__smallLabel">
							<span>${packageDeal.from_airport_name}, ${packageDeal.from_iata}</span>
							</div>
							<div><span class="abResult__bigLabel">${packageDeal.from_city}</span></div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="abResult__summaries">
			<div class="abResult__segmentInfo">
				<div class="abResult__summary">
					<span><span class="icon icon-calendar"></span><span>${packageDeal.trip_length} ${l('global.calendar.days')}</span></span>
					<span><span class="icon icon-user"></span>${packageDeal.adults > 1 ? `${packageDeal.adults} ${l('searchBox.passengers.adults')}` : `${packageDeal.adults} ${l('searchBox.passengers.adult')}`}</span>
					<span><span class="icon icon-flight-n-hotel"></span><span>Hotell + Flyg</span></span>
				</div>
			</div>
		</div>
	</div>
	<div class="abResult__otas">
		<div class="abResult__otaAndBuyPreview">
			<div class="abResult__packagedeal-picturepack">
				${insertIf(packageDeal.images.length, `<img class="abResult__packagedeal-picture" src="/api/CharterImage/${packageDeal.id}/0" width="200" height="130" alt="package">`)}
				${insertIf(packageDeal.images.length > 1, `<img class="abResult__packagedeal-picture" src="/api/CharterImage/${packageDeal.id}/1" width="200" height="130" alt="package">`)}
			</div>
			<div class="packageDeal-summary">
				<div class="packageDeal-specific" data-action="view-all-agencies">
					<span class="hotel-name">
						<span class="icon icon-hotel"></span>
						${packageDeal.hotel_name}
					</span>
					<span class="hotel-room">${packageDeal.room_type}</span>
					<div class="abResult__score">
						${Array.from({ length: Math.floor(packageDeal.hotel_stars / 10) }).map(() => '<span class="icon icon-star-full"></span>').join('')}
					</div>
				</div>
				<div class="packageDeal-ctas">
					<a href="${packageDeal.url}" class="otaLink" target="_blank">
						<div class="abBuyBtn__previewPrice">
							<span class="abBuyBtn__previewPrice--text">Boka nu</span>
							<span>${lc(packageDeal.price)}</span>
						</div>
					</a>
				</div>
			</div>
		</div>
    </div>
</div>
			`,
			className: 'modal--resultModal modal--resultModalPackage',
			footer: `
<button class="abBtn--whiteBlue abBtn abBtn--flex abResult__shareBtn abResult__shareBtn--package" data-url="${packageDeal.url}">
	<div class="shareBtn__popup"></div>
	<span class="icon icon-share-2"></span>
	<span>${l('global.simplePhrases.share')}</span>
</button>
<button class="js-close-modal abBtn abBtn--flex abBtn--blueWhite">
	<span>${l('global.simplePhrases.close')}</span>
</button>
		`,
			hideOnHistoryBack: isMobileView(),
			hideOnSwipe: true,
			id: 'resultModal',
			onClose: () => {
				// Remove custom event listener.
				window.removeEventListener('resize', resizeEvent);
				this.resultModal = null;
			},
			onInit: () => window.addEventListener('resize', resizeEvent),
			resetScroll: true
		});


		return this.resultModal;
	},
	/**
	 * Lookup airline
	 * @param {String} flightCode - Example: EN
	 * @returns {String} Airline
	 */
	getAirline (flightCode) {
		const airlineCode = flightCode.substr(0, 2);
		const airline = resultsRender.response?.airlineDetails?.[airlineCode] || l('showresult.result.unspecifiedAirline');

		if (
			window?.js_params?.missingDetailsReport
			&& resultsRender.response.airlineDetails
			&& !resultsRender.response.airlineDetails[airlineCode]
			&& !resultsRender.missingResultDetails.airlines.includes(airlineCode)
		) {
			resultsRender.missingResultDetails.airlines.push(airlineCode);
			console.warn(`Unknown airline: ${airlineCode}`);
			this.missingResultDetailsTrackers.airlines.reportError(new Error(`Unknown airline: ${airlineCode}`));
		}

		return airline;
	},
	/**
	 * Lookup airport
	 * @param {String} iata
	 * @returns {airportDetails}
	 */
	getAirport (iata) {
		const airport = resultsRender.response?.airportDetails?.[iata] || '';

		if (
			window?.js_params?.missingDetailsReport
			&& resultsRender.response.airportDetails
			&& !resultsRender.response.airportDetails[iata]
			&& !resultsRender.missingResultDetails.airports.includes(iata)
		) {
			resultsRender.missingResultDetails.airports.push(iata);
			console.warn(`Unknown airport: ${iata}`);
			this.missingResultDetailsTrackers.airports.reportError(new Error(`Unknown airport: ${iata}`));
		}

		return airport;
	},
	/**
	 * @param {Object} flight
	 * @param {Number} tripIndex
	 * @param {Number} alternativeIndex
	 * @param {Object} [options = {}]
	 * @returns {Object}
	 */
	getAlternative (trip, tripIndex, alternativeIndex, options = {}) {
		const longestSegment = {
			traveltime: trip.segments?.[0].traveltime,
			flight: trip.segments?.[0].flight
		};
		const tripWithMeta = {
			...options
		};


		const getWaitingTimeStatus = (trip, segmentId) => {
			let status = 'ok';
			if (trip?.longWaitingTime?.includes?.(segmentId)) {
				status = 'long';
			} else if (trip?.shortWaitingTime?.includes?.(segmentId)) {
				status = 'short';
			}
			return status;
		};

		const flightWaitingStops = [];
		let flightStopsCount = 0;
		let currentTimeOfFlight = 0;
		let tripText = '';
		let waitingTimeStatus = 'ok';
		let requiresAirportChange = 0;

		const noOfSegs = trip.segments.length;

		tripWithMeta.bound = tripIndex === 0 ? 'outbound' : 'inbound';
		tripWithMeta.totalTravelTime = getTimeText(trip.traveltime);
		tripWithMeta.segment = [];
		tripWithMeta.hash = trip.hash;
		let totalWaitingTime = 0;

		for (let i = 0; i < noOfSegs; ++i) {
			const completeSegment = trip.segments[i];

			if (completeSegment) {
				const segment = {
					equipment: completeSegment.equipment,
					airline: this.getAirline(completeSegment.flight),
					arrivalAirport: completeSegment.arrivalAirport,
					arrivalCity: this?.response?.meta?.to || window?.js_params?.to?.city,
					arrivalAirportName: this.getAirport(completeSegment.arrivalAirport).airport,
					arrivalDate: completeSegment.arrivalDate,
					arrivalTime: this.getDisplayTime(completeSegment.arrivalDate),
					cabin: completeSegment.cabin,
					departureAirport: completeSegment.departureAirport,
					departureCity: this?.response?.meta?.from || window?.js_params?.from?.city,
					departureAirportName: this.getAirport(completeSegment.departureAirport).airport,
					departureDate: completeSegment.departureDate,
					departureTime: this.getDisplayTime(completeSegment.departureDate),
					flightNo: completeSegment.flight,
					flightTime: getTimeText(completeSegment.traveltime),
					waitingTimeStatus: getWaitingTimeStatus(trip, completeSegment.id)
				};

				// Checks if the segment id is irregular (used in pricecalendar, ex: 878342350_31)
				if (/^\d+_\d+$/.test(completeSegment.id)) {
					let realId = completeSegment.id.split('_');
					realId = realId[realId.length - 1];

					segment.requiresAirportChange = trip?.changeOfAirport?.includes(parseInt(realId)) ? 1 : 0;
				} else {
					segment.requiresAirportChange = trip?.changeOfAirport?.includes(completeSegment.id) ? 1 : 0;
				}

				requiresAirportChange = segment.requiresAirportChange;
				currentTimeOfFlight += completeSegment.traveltime;

				if (['long', 'short'].includes(segment.waitingTimeStatus)) {
					waitingTimeStatus = segment.waitingTimeStatus;
				}

				if (longestSegment.traveltime < completeSegment.traveltime) {
					longestSegment.traveltime = completeSegment.traveltime;
					longestSegment.flight = completeSegment.flight;
				}

				const departureDate = dateParseISO(completeSegment.departureDate);
				const arrivalDate = dateParseISO(completeSegment.arrivalDate);

				segment.localizedDepartureDayString = dateFormat(departureDate, 'PPP');
				segment.localizedArrivalDayString = dateFormat(arrivalDate, 'PPP');
				segment.localizedDepartureDayShortString = dateFormat(departureDate, 'PP');
				segment.localizedArrivalDayShortString = dateFormat(arrivalDate, 'PP');

				if (i < noOfSegs - 1) {
					const nextSegment = trip.segments[i + 1];
					const arrivalTimestamp = parseInt(arrivalDate.getTime());
					const departureTimestamp = parseInt(dateParseISO(nextSegment.departureDate).getTime());
					const waitingTimeSeconds = (departureTimestamp - arrivalTimestamp) / 1000;

					segment.waitTime = getTimeText(waitingTimeSeconds);

					totalWaitingTime += waitingTimeSeconds;

					flightWaitingStops.push({
						airport: completeSegment.arrivalAirport,
						length: segment.waitTime,
						percent: Math.round((currentTimeOfFlight / trip.traveltime) * 100)
					});

					++flightStopsCount;

					currentTimeOfFlight += waitingTimeSeconds;

					tripText = tripText === '' ? completeSegment.arrivalAirport + ': ' + segment.waitTime : '';
				}

				segment.airlineCode = completeSegment.flight.substr(0, 2);

				if (resultsRender.response.airportDetails) {
					segment.departureCity = resultsRender.response.airportDetails?.[completeSegment.departureAirport]?.city || '';
					segment.arrivalCity = resultsRender.response.airportDetails?.[completeSegment.arrivalAirport]?.city || '';
				}

				tripWithMeta.segment[i] = segment;
			}
		}

		tripWithMeta.totalWaitingTime = totalWaitingTime;
		tripWithMeta.totalWaitingTimeText = getTimeText(totalWaitingTime);
		tripWithMeta.airlineCode = longestSegment.flight.substr(0, 2);
		tripWithMeta.airline = this.getAirline(longestSegment.flight);
		tripWithMeta.isAirlinePictureMissing = resultsRender.response?.airlineMissingPictures?.indexOf(tripWithMeta.airlineCode) > -1;
		tripWithMeta.waitingTimeStatus = waitingTimeStatus;
		tripWithMeta.requiresAirportChange = requiresAirportChange;
		tripWithMeta.waiting = {
			airports: flightWaitingStops,
			stops: flightStopsCount,
			tripText: tripText
		};

		return tripWithMeta;
	},
	getFlights (flights) {
		return flights.map((flight) => {
			flight.trips = this.getTripsAndSegmentsForFlight(flight);
			return flight;
		});
	},
	getTripsAndSegmentsForFlight (flight) {
		// If flights trips is an object it has already been processed
		if (typeof flight.trips?.[0] === 'object') {
			return flight.trips;
		}

		// Otherwise we need to process it
		return flight.trips.map((tripId) => {
			const trip = { ...resultsRender.response.trips[tripId] };
			trip.segments = trip.segments.map(segmentId => resultsRender.response.segments[segmentId]);
			return trip;
		});
	},
	/**
	 * @param {import('../pages/showresult').flight} flight
	 * @param {Number} index
	 * @param {Boolean} processAgents
	 * @returns {Object}
	 */
	getFlightRenderData (flight, index, source = 'list') {
		if (!flight) {
			throw new Error(`Undefined flight object passed as argument: ${flight} at index: ${index}`);
		}

		// Checking if the flight is processed already, we should try to come up with a more robust solution
		if (flight?.resultIndex != null) {
			return flight;
		}

		let flightData = {};

		if (flight.agents?.length) {
			flightData = {
				...flight,
				formatedPrice: lc(flight.agents[0].price, true),
				// Force cabin to 4 if unmet class type is present
				cabin: flight?.cabin ?? window?.js_params?.classType,
				resultIndex: index,
				shared: this.isSharedResultView && flight.orgIndex === window?.js_params?.sharedOrgIndex,
				source: source,
				trips: this.getTripsAndSegmentsForFlight(flight)
			};

			flightData.trips = flightData.trips.map((trip, tripIndex) => this.getAlternative(trip, tripIndex));

			flightData.hashes = flightData.trips.map(({ hash }) => hash);

			// Flatten all segments once
			const allSegments = flightData.trips.flatMap(({ segment }) => segment);

			flightData.airlines = joinWithAmp(Array.from(new Set(allSegments.map(({ airline }) => airline))));

			flightData.ariaLabel = this.formatFlightDetails(flightData);

			this.setSafariTravelPartnerLinks(flightData);
		} else {
			flightData = {
				id: flight.id,
				ancient: true,
				trips: flight.trips,
				index: index
			};
		}

		return flightData;
	},
	formatFlightDetails (tripData) {
		const trip = tripData.trips[0];
		const agents = tripData.agents;

		const mainAgent = agents[0];
		const formattedPrice = mainAgent ? `${mainAgent.price} kr` : 'Price not available';

		const totalTravelTime = trip.totalTravelTime;

		// Main Details
		const summary = `Price: ${formattedPrice}. Total travel time: ${totalTravelTime}.`;

		// Breakdown of each leg
		const segmentDetails = trip.segment.map((segment, index) => {
			const departureInfo = `Depart from ${segment.departureCity} (${segment.departureAirport}) on ${segment.localizedDepartureDayString} at ${segment.departureTime}.`;
			const arrivalInfo = `Arrive in ${segment.arrivalCity} (${segment.arrivalAirport}) on ${segment.localizedArrivalDayString} at ${segment.arrivalTime}.`;

			let layoverInfo = '';
			if (index < trip.segment.length - 1) {
				layoverInfo = `Layover at ${segment.arrivalCity} for ${segment.waitTime}.`;
			}

			return `${departureInfo} ${arrivalInfo} ${layoverInfo}`;
		}).join(' ');


		// Generate full text
		const fullText = `${summary} ${segmentDetails}`;

		return fullText;
	},
	/**
	 * @param {String} datetime - Example: "2022-03-08 12:00"
	 * @returns {String} - Example: "12:00"
	 */
	getDisplayTime (datetime) {
		// in order to show local time independently from timezones
		return datetime.split(' ')?.[1] || '';
	},
	/**
	 * @param {"baggage" | "climateCompensated" | "rebookable"} product
	 */
	getPromotedValue (product) {
		switch (product) {
			case 'baggage':
				return 'baggageIncluded';
			case 'cabinBaggage':
				return 'cabinBaggageIncluded';
			case 'climateCompensated':
				return 'carbonOffset';
			case 'rebookable':
				return 'flexTicket';
		}
	},
	/**
	 *
	 * @param {import('../pages/showresult').flight} flight
	 * @param {Number} index
	 * @returns {Object}
	 */
	getReturnTripSegment (flight, index) {
		const arr = [];
		const j = flight.trips[index].length;
		for (let i = 0; i < j; ++i) {
			arr.push(this.getAlternative(flight, index, i));
		}

		return arr;
	},
	/**
	 * @param {import('../pages/showresult').flight} flight
	 * @param {Number} index
	 * @param {Object} options
	 * @returns
	 */
	getRowHtml (flight, index, options = {}) {
		const journeys = this.getFlightRenderData(flight, index);

		const html = renderJourneys([journeys], {
			filters: this.searchParams.filters,
			showEmissions: this.isEmissionSorting || this.isClimateCompensatedFiltered,
			filteredByClimateCompensation: this.isClimateCompensatedFiltered,
			displayFavBtn: window?.js_params?.favoritesEnabled,
			displayShareButton: true,
			...(window?.js_params?.passengers > 1 ? { multiplePassengers: window?.js_params?.passengers } : {}),
			...options
		});

		return html;
	},
	/**
	 * @param {String} agent
	 * @returns {Boolean}
	 */
	isBestAgent (agent) {
		return agent === 'booking';
	},
	/**
	 * @param {String} agent
	 * @returns {Boolean}
	 */
	isDirectBookingAgent (agent) {
		return agent === 'travelpartner';
	},
	/**
	 * @param {flight} flight
	 */
	outclickAgencyResult (flight, tripIndex = 0, eventId) {
		try {
			const agent = flight.agents[tripIndex];

			const showresultID = window?.js_params.urlVars.id;
			const OTA = agent.agent;
			const price = agent.price;


			const peopleCount = window?.js_params?.passengers;
			const trips = [];
			const airlines = [];

			const trip = resultsRender.getTripsAndSegmentsForFlight(flight);

			trip.forEach((trip) => {
				const tripRes = [];

				trip.segments.forEach((segment) => {
					tripRes.push(segment.departureAirport);
					tripRes.push(segment.arrivalAirport);

					airlines.push(segment.flight.substr(0, 2));
				});

				trips.push(tripRes);
			});

			trackEvent.fakeEcommerce(showresultID, tripIndex, OTA, price, peopleCount, airlines, trips, eventId);
		} catch (e) {
			console.log(e);
		}
	},
	/**
	 * @param {import('../pages/showresult').flight} results
	 * @param {Number} page
	 * @param {Element} renderTarget
	 */
	render (flights, page, renderTarget = qs('#results-container'), source = 'list', templateOptions = {}) {
		const journeys = [];
		const options = {
			filters: window?.showresult?.searchParams?.filters || {},
			showEmissions: this.isEmissionSorting || this.isClimateCompensatedFiltered,
			filteredByClimateCompensation: this.isClimateCompensatedFiltered,
			displayFavBtn: window?.js_params.favoritesEnabled,
			displayShareButton: true,
			passengers: window?.js_params?.passengers,
			...(window?.js_params?.passengers > 1 ? { multiplePassengers: window?.js_params?.passengers } : {}),
			...templateOptions
		};

		// Parse flights
		const sharedResultJourney = [];
		for (let i = 0; i < flights.length; i++) {
			const parsedFlight = this.getFlightRenderData(flights[i], i + (resultsRender.response.flights.length - flights.length), source);
			journeys.push(parsedFlight);
			if (parsedFlight.shared) {
				sharedResultJourney.push(parsedFlight);
			}
		}

		// Generate html
		const html = renderJourneys(journeys, options, source);

		// Render DOM
		if (page > 0) {
			renderTarget.insertAdjacentHTML('beforeend', html);
		} else {
			renderTarget.innerHTML = html;
		}

		// Render share result
		if (this.isSharedResultView && page === 0) {
			if (sharedResultJourney.length === 0) {
				this.elems.sharedResult.innerHTML = `<div class="sharedResult__msg">${l('showresult.share.filteredOut')}</div>`;
			} else {
				this.elems.sharedResult.innerHTML = renderJourneys(sharedResultJourney, { ...options, shared: true });
			}
		}
	},
	/**
	 *
	 * @param {Number} index
	 * @param {Element} clickedResult
	 */
	renderResultDetails (index, clickedResult) {
		const filtered = resultsRender.response.flights;
		const journey = this.getFlightRenderData(filtered[index], index);
		const filters = this.searchParams.filters || {};

		const options = {
			filters: filters,
			filteredByClimateCompensation: resultsRender.isClimateCompensatedFiltered,
			showEmissions: resultsRender.isEmissionSorting || resultsRender.isClimateCompensatedFiltered
		};

		const html = renderModal(journey, options);

		clickedResult.classList.add('abResult--expanded');

		return html;
	},
	/**
	 * @param {Object} data
	 * @param {Object} searchParams
	 */
	setData (data, searchParams = { filters: {} }) {
		resultsRender.response = data;
		this.searchParams = searchParams;
	},
	/**
	 * Modifies flightData.agents with a patched bookingUrl
	 * @param {Object} flightData - processed flight (return value from getFlightData)
	 */
	setSafariTravelPartnerLinks (flightData) {
		if (!isSafari()) {
			return;
		}

		for (let i = 0; i < flightData.agents.length; i++) {
			if (this.isDirectBookingAgent(flightData.agents[i].agent)) {
				const url = encodeURIComponent(Base64.encode(`${document.location.origin}${flightData.agents[i].bookingUrl}`));

				flightData.agents[i].bookingUrl = `${window?.js_params.travelpartnerSafariBounce}?url=${url}`;
			}
		}
	},

	getShowresultUrl (searchID, sorting = getUrlVars()?.sorting) {
		const baseUrl = linkPrefix.base ? `${linkPrefix.base}` : location.origin;
		let url = `${baseUrl}/${lLink('global.navigation.resultsLink')}?id=${searchID}`;

		if (sorting) {
			url += `&sorting=${sorting}`;
		}
		if (location.href.includes('&filter=')) {
			url += `&filter=${location.href.split('&filter=')[1].split('&')[0]}`;
		}

		return url;
	},

	/**
	 * Creates a new modal instance
	 * @param {int} responseId
	 * @param {object} flight
	 * @param {int} index
	 * @param {object} filters
	 * @param {object} modalOptions
	 * @param {object} options
	 * @param {string} source
	 * @returns
	 */
	renderModal (responseId, flight, index, filters, modalOptions, options, source = 'list', sorting = getUrlVars()?.sorting) {
		this.searchParams.filters = filters;

		this.resultModal?.close?.();

		// Parse flight
		const journey = this.getFlightRenderData(flight, index, source);

		// Generate html
		const html = `<div class="abResult abResult--modal" data-index="${index}">${renderModal(journey, { filters, index, ...options })}</div>`;


		// Open modal
		this.resultModal = new Modal({
			body: html,
			className: 'modal--resultModal',
			footer: `
<button class="js-close-modal abBtn abBtn--whiteBlue abBtn--flex">
	<span>${l('global.simplePhrases.close')}</span>
</button>
<a
	id="price-calendar-show-more-results"
	href="${this.getShowresultUrl(responseId, sorting)}"
	class="abBtn abBtn--blueWhite abBtn--flex">
	<span>${l('global.simplePhrases.showMoreResult')}</span>
</a>`,
			hideHeader: true,
			hideMobileUp: false,
			hideOnSwipe: true,
			hideOnHistoryBack: isMobileView(),
			id: 'resultModal_1',
			onClose: () => {
				// Remove custom event listener.
				// window.removeEventListener('resize', resizeEvent);
				this.resultModal = null;
			},
			// onInit: () => window.addEventListener('resize', resizeEvent),
			resetScroll: true,
			...modalOptions
		});

		return this.resultModal;
	},

	/**
	 * @param {Element} clickedResult
	 */
	async toggleTripDetails (clickedResult) {
		if (clickedResult.closest('#resultModal')) {
			return;
		}

		// Toggle expanded class
		const isExpanded = clickedResult.classList.contains('abResult--expanded');

		// Close previous modal
		if (isExpanded && this?.resultModal?.isOpen) {
			await this.resultModal.close();
		}

		// Get current result trip details
		const resultTripDetails = clickedResult.querySelector('.abResult__flightDetails');

		// Get current index
		const resultIndex = clickedResult.dataset.index;

		// Generate result details preview
		let template = this.renderResultDetails(resultIndex, clickedResult);

		// Render for tablet/desktop.
		if (resultTripDetails.children.length === 0) {
			resultTripDetails.innerHTML = template;
		}

		// Render modal for mobile.
		if (this.resultModal) {
			await this.resultModal.close();
			this.resultModal = null;
		}

		template = `<div class="abResult abResult--modal" data-index="${resultIndex}">${template}</div>`;

		this.buildResultModal(template, resultIndex).open();
	},

	// Share link functions
	/**
	 *
	 * @param {Number} resultIndex
	 * @returns
	 */
	getSharedResultURL (resultIndex) {
		const { orgIndex, lowestprice } = resultsRender.response.flights[resultIndex];
		const baseUrl = this.getShowresultUrl(this.response.meta.responseId, this.searchParams.sorting);
		return setUrlParameter(baseUrl, 'shared', `${orgIndex}-${lowestprice}`);
	},

	/**
	 *
	 * @param {Element} shareBtn
	 * @param {String} sharedURL
	 */
	openManualCopyPopup (shareBtn, sharedURL) {
		const popup = shareBtn.querySelector('.shareBtn__popup');
		popup.classList.add('shareBtn--manualCopy');
		if (popup.innerHTML === '') {
			popup.innerHTML = `<div class="popup__title">${l('showresult.share.manualCopyTitle')}</div><input type="url" class="sharedUrl__field" value="${sharedURL}">`;
		}
		shareBtn.classList.add('popup--visible');
	},

	removeSharedResult () {
		// Update URL
		urlParameterDelete('shared');

		// Remove shared result from DOM
		this.elems.sharedResultContainer.remove();

		this.isSharedResultView = false;
		delete this.searchParams.sharedOrgIndex;
		EventDispatcher.dispatch('updateResults', { source: 'list', params: this.searchParams });
		document.body.classList.remove('shared-result');
	},
	mergeResponse (meta, mergeAllMeta = false, mergeFlights = false) {
		if (!meta) {
			return;
		}

		let uniqueTimesMerged = false;
		if (mergeFlights && resultsRender?.response?.flights && meta.flights) {
			resultsRender.response.flights = [...resultsRender.response.flights, ...meta.flights];
		}

		for (const key in meta) {
			if (key === 'data') {
				continue;
			}

			// If empty array or empty object set the initial state
			if ((Array.isArray(resultsRender.response?.[key]) && resultsRender.response?.[key].length === 0) || isEmptyObject(resultsRender.response?.[key] || {})) {
				resultsRender.response[key] = meta[key];
				continue;
			}

			// Now we start to merge

			// If plain object
			if (meta[key].constructor === Object) {
				resultsRender.response[key] = Object.assign(resultsRender.response[key], meta[key]);
				continue;
			}

			// All the logic below is for forceNearbyDates. We don't merge for normal results as it causes weird behaviour.
			if (!getUrlVars()?.forceNearbyDates && !mergeAllMeta) {
				console.log('Skipping merge for key:', key);
				continue;
			}

			const defaultSort = (a, b) => a - b;

			// If primitive array
			if (Array.isArray(meta[key]) && meta[key].length > 0 && typeof meta[key][0] !== 'object') {
				resultsRender.response[key] = arrayMergePrimitive(resultsRender.response[key], meta[key]).sort(defaultSort);
				continue;
			}

			// If we merge for none faulty results we get weird behaviour, only merge these filters for this edge case.
			if (['uniqueTimes', 'uniqueTimesOffset'].includes(key)) {
				// Check if we have already merged the uniqueTimes
				const isAlreadyMerged = resultsRender.response[key].every((item, index) => {
					const { departures: metaDep = [], arrivals: metaArr = [] } = meta[key][index] || {};
					const { departures: itemDep = [], arrivals: itemArr = [] } = item || {};
					return metaDep.every(d => itemDep.includes(d)) && metaArr.every(a => itemArr.includes(a));
				});

				if (key === 'uniqueTimes' && isAlreadyMerged) {
					uniqueTimesMerged = true;
					continue;
				}

				if (key === 'uniqueTimesOffset' && uniqueTimesMerged) {
					continue;
				}

				resultsRender.response[key] = resultsRender.response[key].map((item, index) => {
					return {
						departures: [...item.departures, ...meta[key][index].departures].sort(defaultSort),
						arrivals: [...item.arrivals, ...meta[key][index].arrivals].sort(defaultSort)
					};
				});

				continue;
			}

			// If the key is an array of objects merge by a specific key
			if (['uniqueAirportStops', 'uniqueAirportDepartures', 'uniqueAirportArrivals'].includes(key)) {
				const mergeArrayOfObject = (arr1, arr2, key) => {
					const map = new Map();
					arr1.forEach(item => map.set(item[key], item));
					arr2.forEach(item => map.set(item[key], item));
					return Array.from(map.values());
				};

				resultsRender.response[key] = mergeArrayOfObject(resultsRender.response[key], meta[key], 'iata');
				continue;
			}
		}
	},

	init () {
		if (this.initialised) {
			return;
		}
		this.initialised = true;

		resultsRender.bindEvents();
	}

};

window.resultsRender = resultsRender;

function joinWithAmp (arr) {
	if (arr.length <= 1) {
		return arr[0] || '';
	}
	return arr.slice(0, -1).join(', ') + ' & ' + arr[arr.length - 1];
}
